/**
 * The live-combat room connection (`backend/src/game/battleground/`).
 *
 * ─── Why this file exists at all ────────────────────────────────────────────
 * The server has run a complete duel simulation since the Battleground story
 * landed — movement, arc resolution, crits, armour, ammo, bots, forfeits — and
 * the browser has never once connected to it. `ArenaMatchView.seat` was minted,
 * shipped to the client and dropped on the floor; the player queued, accepted,
 * watched an 8-second countdown and then stood in the settlement while a bot
 * killed their idle character somewhere they could not see.
 *
 * ─── Transport, and why it is not the game's socket ─────────────────────────
 * This is the ONE system in the game that does not speak Socket.io
 * (`design/adr/ADR-0001-battleground-colyseus-transport.md`). `base.gateway.ts`
 * caps inbound events at 30 per 60 seconds per user — one every two seconds —
 * and lifting that means editing `core/`, which a game repo may not do. So the
 * live session runs on an embedded Colyseus server with its own listener,
 * proxied by Caddy at `/bg`.
 *
 * Per the ADR, the Colyseus client belongs HERE, in `engine/`, and never in
 * `core/network/` beside `socket.ts` — putting it there would be exactly the
 * boundary violation the dedicated transport exists to avoid.
 *
 * ─── Three things verified against the installed colyseus.js 0.16.22 source,
 *     not recalled ─────────────────────────────────────────────────────────
 * The ADR flags its own API surface as "recalled, not verified" and asks for
 * this check as the first implementation step. Findings:
 *
 * 1. **The endpoint is the SERVER's to choose.** `Client.buildEndpoint` reads
 *    `room.publicAddress` and only falls back to the client's own
 *    hostname/port/pathname when the server did not set one. Ours always does
 *    (`battleground.bootstrap.ts` → `localhost:3010` in dev, `<host>/bg` in
 *    production). What the client contributes is the `secure` flag, which
 *    decides `ws://` vs `wss://` — and `colyseus.js`'s own default endpoint
 *    already derives that from `window.location.protocol`. Hence the bare
 *    `new Client(...)` below with only an optional override.
 * 2. **The seat token does not travel with the client.** `reserveSeatFor(room,
 *    {seatToken, userId, instanceId})` stores those options server-side; on
 *    join, `Room._onJoin` reads them back out of `reservedSeats[sessionId]` and
 *    passes them to `onAuth`. So `consumeSeatReservation(reservation)` is the
 *    whole handshake — re-sending `seatToken` as a join option would be
 *    ignored, and worse, would imply the token is client-supplied when the
 *    room's replay protection depends on it not being.
 * 3. **No schema classes are shared.** Passing no `rootSchema` makes the
 *    serializer build its decoder from the reflection data the server sends in
 *    the handshake (`SchemaSerializer.handshake` → `Reflection.decode`). A
 *    hand-mirrored copy of `BattlegroundRoomState` would be a second source of
 *    truth that fails silently the day a field is added server-side.
 *
 * ─── Snapshots, not live schema objects ─────────────────────────────────────
 * The decoder mutates its state objects in place, so the room's `players` map
 * is the same reference before and after every patch. Handing that to the store
 * would defeat every `===` check in the subscriber chain — the state would
 * always look unchanged while always being different. `toSnapshot` copies out.
 */
import { Client, Room } from 'colyseus.js';

import { createStore } from '../../core/state/store';
import { socket } from '../../core/network/socket';
import type {
  AttackResult, BattlegroundState, HealCancelled, HealResult, HealStarted, OutOfAmmo, PvpConcluded,
  RoomEnemy, RoomPlayer, RoomSnapshot, RoomVec3, WeaponSlotId,
} from './battleground-state.types';
import { EMPTY_SNAPSHOT } from './battleground-state.types';

const INITIAL_STATE: BattlegroundState = {
  status: 'idle',
  matchId: null,
  selfId: null,
  snapshot: EMPTY_SNAPSHOT,
  error: null,
  result: null,
};

export const battlegroundStateStore = createStore<BattlegroundState>({ ...INITIAL_STATE });

/**
 * Movement send rate.
 *
 * The ADR budgets "~10 Hz client→server" and that is a ceiling, not a target to
 * hit every frame: at 60 fps an unthrottled send would be six times the budget
 * and would put the duel's traffic an order of magnitude above every other
 * system in the game. The server re-validates positions regardless, so a
 * dropped intermediate frame costs nothing but interpolation smoothness.
 */
const MOVE_INTERVAL_MS = 100;

let room: Room | null = null;
let lastMoveSentAt = 0;

// ─── Ephemeral combat events ────────────────────────────────────────────────
/**
 * `attackResult` / `healResult` are EVENTS, not state, and deliberately do not
 * go through the store.
 *
 * A floating damage number is a thing that happened once; putting it in the
 * store would mean every subscriber re-renders on every swing, and a late
 * subscriber would replay a hit that landed a minute ago. The renderer
 * subscribes to these directly and drops them on the floor when nothing is
 * listening — which is the correct behaviour for a hit landed while the scene
 * is being torn down.
 */
type AttackListener = (result: AttackResult) => void;
type HealListener = (result: HealResult) => void;
type OutOfAmmoListener = (result: OutOfAmmo) => void;
type HealStartedListener = (result: HealStarted) => void;
type HealCancelledListener = (result: HealCancelled) => void;
const attackListeners = new Set<AttackListener>();
const healListeners = new Set<HealListener>();
const outOfAmmoListeners = new Set<OutOfAmmoListener>();
const healStartedListeners = new Set<HealStartedListener>();
const healCancelledListeners = new Set<HealCancelledListener>();

export function onAttackResult(fn: AttackListener): () => void {
  attackListeners.add(fn);
  return () => { attackListeners.delete(fn); };
}

export function onHealResult(fn: HealListener): () => void {
  healListeners.add(fn);
  return () => { healListeners.delete(fn); };
}

/**
 * The trigger was pulled on an empty weapon.
 *
 * An event and not state for the same reason as the two above — it is a thing
 * that happened once, and the red flash it drives is a one-shot. The room
 * sends it to the shooter alone, so a listener never has to check whose it is.
 */
export function onOutOfAmmo(fn: OutOfAmmoListener): () => void {
  outOfAmmoListeners.add(fn);
  return () => { outOfAmmoListeners.delete(fn); };
}

/**
 * A heal has begun and will land in `durationMs`.
 *
 * The live countdown is NOT here — it rides `RoomPlayer.healingRemaining` on
 * every snapshot, so a bar drawn from it stays in step with the server's own
 * clock instead of with a local `setInterval` that drifts. This event carries
 * the things a snapshot cannot: which item is running, and what to call it.
 */
export function onHealStarted(fn: HealStartedListener): () => void {
  healStartedListeners.add(fn);
  return () => { healStartedListeners.delete(fn); };
}

/** The channel was cut short — the player attacked, or swapped what was in their hands. */
export function onHealCancelled(fn: HealCancelledListener): () => void {
  healCancelledListeners.add(fn);
  return () => { healCancelledListeners.delete(fn); };
}

// ─── Snapshot projection ────────────────────────────────────────────────────
function vec3(v: unknown): RoomVec3 {
  const source = (v ?? {}) as Partial<RoomVec3>;
  return { x: Number(source.x) || 0, y: Number(source.y) || 0, z: Number(source.z) || 0 };
}

/**
 * Colyseus collections are `MapSchema`/`ArraySchema`, which are iterable but
 * are not a `Map`/`Array` — and, decoded by reflection, arrive as whatever the
 * server's schema says rather than as a type we declared. Read defensively:
 * a field this client does not know about must be ignored, never thrown on.
 */
function collect<T>(collection: unknown, project: (entry: Record<string, unknown>) => T): T[] {
  const out: T[] = [];
  const iterable = collection as { forEach?: (fn: (value: unknown) => void) => void } | undefined;
  if (!iterable || typeof iterable.forEach !== 'function') return out;
  iterable.forEach((entry) => {
    if (entry && typeof entry === 'object') out.push(project(entry as Record<string, unknown>));
  });
  return out;
}

function optionalNumber(raw: unknown): number | undefined {
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined;
}

/** @see the `weaponKey` note in `toSnapshot` — `''` is the room's null. */
function optionalString(raw: unknown): string | undefined {
  return typeof raw === 'string' && raw.length > 0 ? raw : undefined;
}

/**
 * Narrows the room's plain string to the union, rejecting anything else.
 *
 * A newer server could add a fourth selectable slot; this client must ignore
 * it rather than hand an unknown value to the hotbar, which would light no
 * cell at all and look like the bar had stopped working.
 */
function weaponSlot(raw: unknown): WeaponSlotId | undefined {
  return raw === 'UNARMED' || raw === 'PRIMARY' || raw === 'SECONDARY' ? raw : undefined;
}

export function toSnapshot(state: unknown): RoomSnapshot {
  const s = (state ?? {}) as Record<string, unknown>;
  return {
    freezeRemaining: Number(s.freezeRemaining) || 0,
    alerted: Boolean(s.alerted),
    enemies: collect<RoomEnemy>(s.enemies, (e) => ({
      id: String(e.id ?? ''),
      position: vec3(e.position),
      hp: Number(e.hp) || 0,
      armor: optionalNumber(e.armor),
      maxHp: optionalNumber(e.maxHp),
    })),
    players: collect<RoomPlayer>(s.players, (p) => ({
      userId: String(p.userId ?? ''),
      position: vec3(p.position),
      facing: vec3(p.facing),
      hp: Number(p.hp) || 0,
      armor: optionalNumber(p.armor),
      maxHp: optionalNumber(p.maxHp),
      maxArmor: optionalNumber(p.maxArmor),
      // Empty string is the room's "no item key" (Colyseus `@type('string')`
      // has no null), and it must project to `undefined` rather than to `''`
      // — `weapon-sound.ts` treats a falsy key as "fall back to ammoType",
      // and `''` would read as a real key that matches no recording.
      weaponSlot: weaponSlot(p.weaponSlot),
      weaponKey: optionalString(p.weaponKey),
      weaponKind: optionalString(p.weaponKind),
      weaponAmmoType: optionalString(p.weaponAmmoType),
      arcLength: optionalNumber(p.arcLength),
      arcWidth: optionalNumber(p.arcWidth),
      ammoRemaining: optionalNumber(p.ammoRemaining),
      healingRemaining: optionalNumber(p.healingRemaining),
      presetId: optionalString(p.presetId),
      colorScheme: optionalNumber(p.colorScheme),
      // `''` -> `[]`, not `['']`: a fighter wearing nothing is a real state
      // (the unarmed newbies on the bot roster are in it), and a single empty
      // string would be handed to the garment loader as an item key.
      equippedKeys: String(p.equippedKeys ?? '').split(',').filter(Boolean),
    })),
    exfilProgress: Number(s.exfilProgress) || 0,
    timeRemaining: Number(s.timeRemaining) || 0,
  };
}

// ─── Connection ─────────────────────────────────────────────────────────────
function endpoint(): string | undefined {
  // Undefined on purpose when unset: colyseus.js's own default derives
  // `ws(s)://<host>` from `window.location`, which is what decides the secure
  // flag. The host and path themselves come from the server's `publicAddress`
  // (see this file's header), so there is nothing here worth hardcoding.
  const configured = import.meta.env.VITE_BATTLEGROUND_URL;
  return typeof configured === 'string' && configured.length > 0 ? configured : undefined;
}

// ─── Coming back after a reload ─────────────────────────────────────────────
// Janeks, 2026-10-20: *"i refreshed the page and got back to settlement, not
// the PVP arena that was initiated."* The seat is single-use, so a reloaded
// page cannot sit back down with it. The room instead holds a dropped
// duellist's seat for `PVP_RECONNECT_GRACE_S` (battleground.room.ts), and the
// way back in is `room.reconnectionToken`, kept per tab in sessionStorage and
// tied to the match so a stale token never touches another duel.
const RECONNECT_KEY = 'ah:duel-reconnect';

function saveReconnect(matchId: string, token: string): void {
  try { sessionStorage.setItem(RECONNECT_KEY, JSON.stringify({ matchId, token })); } catch { /* storage off */ }
}

function readReconnect(matchId: string): string | null {
  try {
    const saved = JSON.parse(sessionStorage.getItem(RECONNECT_KEY) ?? 'null') as { matchId?: string; token?: string } | null;
    return saved?.matchId === matchId && typeof saved.token === 'string' ? saved.token : null;
  } catch {
    return null;
  }
}

/**
 * Where `client.reconnect()` sends its HTTP matchmaker request.
 *
 * A fresh join never needs this: `consumeSeatReservation` builds its socket
 * URL from the reservation's own `publicAddress` (see `endpoint()`). A
 * reconnect first POSTs `/matchmake/reconnect/<roomId>` to the CLIENT's base,
 * which by default is the page origin, so the request hit the frontend and
 * 404'd (found live, 2026-10-20). The reservation from the same match still
 * names the room server: `localhost:3010` in dev, `<host>/bg` behind Caddy.
 */
function matchmakerEndpoint(seatReservation: unknown): string | undefined {
  const configured = endpoint();
  if (configured) return configured;
  const address = (seatReservation as { room?: { publicAddress?: unknown } } | null)?.room?.publicAddress;
  if (typeof address !== 'string' || !address) return undefined;
  return `${window.location.protocol === 'https:' ? 'https' : 'http'}://${address}`;
}

function clearReconnect(): void {
  try { sessionStorage.removeItem(RECONNECT_KEY); } catch { /* storage off */ }
}

/**
 * Set while the page unloads. `main.ts` tears the game down on
 * `beforeunload`, and that teardown calls `leaveDuel`: a consented
 * `room.leave()` is a forfeit on the spot, so a reload must leave the room as
 * a dropped connection instead. Capture phase, so this runs before main.ts's
 * listener on the same target.
 */
let pageUnloading = false;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => { pageUnloading = true; }, { capture: true });
  window.addEventListener('pagehide', () => { pageUnloading = true; }, { capture: true });
}

/**
 * Joins the duel's room with the seat the arena minted — or, after a reload
 * into the same match, takes the held seat back with the reconnection token.
 *
 * Resolves once the room is joined; rejects are absorbed into `status:'failed'`
 * plus a player-facing `error` rather than thrown, because every caller is a UI
 * mount path with nowhere useful to propagate to.
 */
export async function joinDuel(
  seatReservation: unknown,
  selfId: string,
  matchId: string,
): Promise<boolean> {
  if (room) await leaveDuel();

  // Module state, and therefore stale from the LAST duel unless it is cleared
  // here: the frame loop starts streaming the moment the view mounts, and the
  // first `move` is what establishes the server's picture of where you are
  // during the freeze. `resetBattleground` also clears it, but that only runs
  // on teardown of the whole mount.
  lastMoveSentAt = 0;

  battlegroundStateStore.set((s) => ({
    ...s,
    status: 'joining',
    matchId,
    selfId,
    snapshot: EMPTY_SNAPSHOT,
    error: null,
    result: null,
  }));

  try {
    const client = new Client(endpoint());
    // No rootSchema — decoded by reflection. See this file's header, finding 3.
    let joined: Room | null = null;
    const token = readReconnect(matchId);
    if (token) {
      try {
        joined = await new Client(matchmakerEndpoint(seatReservation)).reconnect(token);
      } catch (err) {
        // The grace lapsed or the duel is over; the seat is the only way left.
        console.warn('[battleground] could not reconnect to the duel room', { matchId }, err);
        clearReconnect();
      }
    }
    joined ??= await client.consumeSeatReservation(seatReservation as never);
    room = joined;
    wireRoom(joined);
    saveReconnect(matchId, joined.reconnectionToken);
    battlegroundStateStore.set((s) => ({ ...s, status: 'joined' }));
    return true;
  } catch (err) {
    room = null;
    // ─── The reservation is printed with the failure, not just the error ────
    // `seat reservation expired` is Colyseus's message for TWO different
    // things (`@colyseus/ws-transport`): the room was not found on this
    // process at all, or it was found and `hasReservedSeat(sessionId)` said
    // no. Without the ids you cannot tell a disposed room from a stale seat,
    // and the two have opposite fixes — it cost several verification runs on
    // 2026-09-19 to narrow it even by half.
    //
    // Shaped defensively: the reservation is an opaque blob from the server
    // and this is an error path, so a missing field must never throw over the
    // top of the error it is trying to describe.
    const res = seatReservation as { room?: { roomId?: string; processId?: string }; sessionId?: string } | null;
    console.error(
      '[battleground] could not join the duel room',
      { roomId: res?.room?.roomId, processId: res?.room?.processId, sessionId: res?.sessionId, matchId },
      err,
    );
    battlegroundStateStore.set((s) => ({
      ...s,
      status: 'failed',
      // ADR-0001: Colyseus is WebSocket-only while the rest of the game falls
      // back to polling, so this is a real and reachable outcome for a player
      // on a restrictive network — not a should-never-happen.
      error: 'Could not reach the arena. Your network may be blocking the connection.',
    }));
    return false;
  }
}

function wireRoom(joined: Room): void {
  joined.onStateChange((state) => {
    battlegroundStateStore.set((s) => ({ ...s, snapshot: toSnapshot(state) }));
  });

  joined.onMessage('attackResult', (payload: AttackResult) => {
    for (const fn of attackListeners) fn(payload);
  });

  joined.onMessage('healResult', (payload: HealResult) => {
    for (const fn of healListeners) fn(payload);
  });

  joined.onMessage('outOfAmmo', (payload: OutOfAmmo) => {
    for (const fn of outOfAmmoListeners) fn(payload);
  });

  joined.onMessage('healStarted', (payload: HealStarted) => {
    for (const fn of healStartedListeners) fn(payload);
  });

  joined.onMessage('healCancelled', (payload: HealCancelled) => {
    for (const fn of healCancelledListeners) fn(payload);
  });

  // `itemUsed` is deliberately NOT surfaced. The consumable's visible effect
  // is the `healResult` that follows it and the inventory sheet the server
  // pushes on its own; a second "you used a bandage" line would be the same
  // news twice. The room sends it so a future hotbar can un-grey its slot
  // without waiting for a sheet round-trip.

  joined.onError((code, message) => {
    console.error('[battleground] room error', code, message);
    battlegroundStateStore.set((s) => ({
      ...s,
      status: 'failed',
      error: 'The arena connection failed.',
    }));
  });

  joined.onLeave(() => {
    room = null;
    // NOT an error: the room disposing itself is the normal end of every duel
    // — the outcome arrives separately on `battleground:pvpConcluded`. Only
    // move off a live status, so a `failed` set by `onError` is not overwritten
    // by the close that follows it.
    battlegroundStateStore.set((s) => (
      s.status === 'joined' || s.status === 'joining' ? { ...s, status: 'left' } : s
    ));
  });
}

export async function leaveDuel(): Promise<void> {
  const current = room;
  room = null;
  if (!current) return;
  // A reload is not a decision to leave: keep the token, and close as a
  // dropped connection so the room holds the seat (see `pageUnloading`).
  if (!pageUnloading) clearReconnect();
  try {
    await current.leave(!pageUnloading);
  } catch {
    // Leaving a room that is already gone is the common case on teardown.
  }
}

// ─── Outbound ───────────────────────────────────────────────────────────────
/**
 * Throttled to `MOVE_INTERVAL_MS`. Safe to call every frame — that is the
 * intended usage, and the throttle living here rather than at the call site is
 * what stops a second caller from quietly doubling the rate.
 */
export function sendMove(position: RoomVec3, facing: RoomVec3, now = Date.now()): void {
  if (!room) return;
  if (now - lastMoveSentAt < MOVE_INTERVAL_MS) return;
  lastMoveSentAt = now;
  room.send('move', { position, facing });
}

/**
 * No client-side cadence gate.
 *
 * The weapon's own `cadenceMs` is enforced in the room (ADR risk #6: "attack
 * should be gated by weapon cadence anyway, not just rate-limited"), and a
 * second copy here would be a tuning value the client could disagree with. A
 * rejected swing simply produces no `attackResult`.
 */
export function sendAttack(aimDirection: RoomVec3): void {
  room?.send('attack', { aimDirection });
}

export function sendForfeit(): void {
  room?.send('forfeit', {});
}

/**
 * Put bare hands, the PRIMARY or the SECONDARY in this fighter's hands —
 * hotbar keys 0/1/2.
 *
 * Fire-and-forget: the room answers by changing `PlayerState`, which arrives
 * as the next snapshot, so there is nothing to await and no optimistic local
 * copy to reconcile. A slot the player does not actually have is ignored
 * server-side rather than rejected.
 */
export function sendSelectSlot(slot: WeaponSlotId): void {
  room?.send('selectSlot', { slot });
}

/**
 * Burn one carried consumable — hotbar keys 3-9.
 *
 * `userItemId` is the `UserItem` row, which the client already holds on the
 * inventory sheet. The room re-reads and re-validates it (it is the only
 * identifier a client could forge into someone else's bag), so this sends the
 * id and asserts nothing.
 */
export function sendUseItem(userItemId: string): void {
  room?.send('useItem', { userItemId });
}

// ─── Outcome (Socket.io, not the room) ──────────────────────────────────────
let wired = false;

/**
 * Subscribes to the duel's outcome.
 *
 * This is on the GAME socket, not the room: by the time a winner is known the
 * room is disposing, and the result is posted by `BattlegroundService` after it
 * has applied XP, health and durability. The client subscribed to none of the
 * three `battleground:*` events before this — which is why a player could lose
 * a duel, a bag and 90% of their HP with no message of any kind.
 */
export function connectBattlegroundOutcome(): () => void {
  if (wired) return () => {};
  wired = true;

  const onConcluded = (payload: PvpConcluded) => {
    battlegroundStateStore.set((s) => ({ ...s, result: payload }));
  };

  socket.on('battleground:pvpConcluded', onConcluded);

  return () => {
    socket.off('battleground:pvpConcluded', onConcluded);
    wired = false;
  };
}

export function clearDuelResult(): void {
  battlegroundStateStore.set((s) => ({ ...s, result: null }));
}

export function resetBattleground(): void {
  void leaveDuel();
  lastMoveSentAt = 0;
  battlegroundStateStore.set({ ...INITIAL_STATE });
}

/** Test seam — the room handle is module-private by design. */
export function __hasRoom(): boolean {
  return room !== null;
}
